/**
 * stepFlattener.ts
 *
 * Pure function: converts execute.steps[] into a flat navigable array
 * with substep grouping, cycle/batch annotations, and plan-step matching.
 */

import type { RunStageStep, FlatExecuteStep, PlanEntity } from '@/types/run-stage.types';

/**
 * Sort steps by executeOrder → order → creationOrder.
 */
function sortSteps(steps: RunStageStep[]): RunStageStep[] {
  return [...steps].sort((a, b) => {
    const aOrder = a.executeOrder ?? a.order ?? a.creationOrder ?? 0;
    const bOrder = b.executeOrder ?? b.order ?? b.creationOrder ?? 0;
    if (aOrder !== bOrder) return aOrder - bOrder;
    // Secondary sort by cloneNumber for batch steps
    return (a.cloneNumber ?? 0) - (b.cloneNumber ?? 0);
  });
}

/**
 * Build a map of planSteps keyed by stepId + cloneNumber for Actual vs Defined matching.
 */
function buildPlanStepMap(plan: PlanEntity | null): Map<string, RunStageStep> {
  if (!plan?.steps) return new Map();
  const map = new Map<string, RunStageStep>();
  for (const s of plan.steps) {
    map.set(`${s.stepId}::${s.cloneNumber ?? 0}`, s);
  }
  return map;
}

/**
 * Flatten execute steps into a navigable array.
 *
 * 1. Sort by executeOrder → order → creationOrder
 * 2. Identify substeps (isCompoundSubstep / parentStepId)
 * 3. Group cycles by cycleGroupId
 * 4. Annotate batches by cloneNumber
 * 5. Build pill labels: "1", "1.1", "1.2", "2", etc.
 * 6. Match each step to its plan counterpart
 */
export function flattenExecuteSteps(
  executeSteps: RunStageStep[],
  plan: PlanEntity | null
): FlatExecuteStep[] {
  const sorted = sortSteps(executeSteps);
  const planMap = buildPlanStepMap(plan);
  const result: FlatExecuteStep[] = [];

  // Track parent step indices for substep labeling
  let currentParentNumber = 0;
  let lastParentStepId: string | null = null;
  const substepCounts = new Map<string, number>();

  for (const step of sorted) {
    const isSubstep = Boolean(step.isCompoundSubstep && step.parentStepId);
    const planKey = `${step.stepId}::${step.cloneNumber ?? 0}`;
    const planStep = planMap.get(planKey);

    let pillLabel: string;
    let parentIndex = -1;

    if (isSubstep && step.parentStepId) {
      // Find parent index in result
      parentIndex = result.findIndex(
        (r) => r.step.stepId === step.parentStepId && !r.isSubstep
      );
      const parentLabel = parentIndex >= 0 ? result[parentIndex].pillLabel : String(currentParentNumber);
      const count = (substepCounts.get(step.parentStepId) ?? 0) + 1;
      substepCounts.set(step.parentStepId, count);
      pillLabel = `${parentLabel}.${count}`;
    } else if (step.isCycle && step.orderString && /^\d+\.\d+$/.test(step.orderString)) {
      // Cycle-expanded step: use the pre-computed orderString (e.g. "1.2")
      pillLabel = step.orderString;
    } else {
      // Top-level step
      currentParentNumber++;
      lastParentStepId = step.stepId;
      substepCounts.set(step.stepId, 0);
      pillLabel = String(currentParentNumber);
    }

    // Cycle label
    let cycleLabel: string | undefined;
    if (step.cycleOrder != null && step.cycleCount != null && step.cycleCount > 1) {
      cycleLabel = `Cycle ${step.cycleOrder}/${step.cycleCount}`;
    }

    // Batch label
    let batchLabel: string | undefined;
    if (step.batchNumber != null && step.batchNumber > 0) {
      batchLabel = `Batch ${step.batchNumber}`;
    }

    // Repeat indicator
    const repeatSuffix = step.isRepeated ? ` (Repeat${step.repeatOrder ? ` #${step.repeatOrder}` : ''})` : '';

    const displayName = `${pillLabel}. ${step.stepName || step.stepId}${repeatSuffix}`;

    result.push({
      step,
      pillLabel,
      displayName,
      isSubstep,
      parentIndex,
      cycleLabel,
      batchLabel,
      planStep,
      checkpoints: [],
      machineChecklists: step.machines?.flatMap((m) => m.usageInstructions?.checklists ?? []) ?? [],
    });
  }

  return result;
}

/**
 * Find the index of the active step (first non-COMPLETED, non-SKIPPED).
 */
export function findActiveStepIndex(flatSteps: FlatExecuteStep[]): number {
  const idx = flatSteps.findIndex(
    (f) => !f.step.completed && f.step.status !== 'COMPLETED' && f.step.status !== 'SKIPPED'
  );
  return idx >= 0 ? idx : 0;
}

/**
 * Compute step stats from execute steps.
 */
export interface StepStats {
  total: number;
  completed: number;
  pending: number;
  skipped: number;
  repeated: number;
  inProgress: number;
}

export function computeStepStats(steps: RunStageStep[]): StepStats {
  const total = steps.length;
  let completed = 0;
  let skipped = 0;
  let repeated = 0;
  let inProgress = 0;

  for (const s of steps) {
    if (s.status === 'COMPLETED' || s.completed) completed++;
    else if (s.status === 'SKIPPED') skipped++;
    else if (s.status === 'IN_PROGRESS') inProgress++;
    if (s.isRepeated) repeated++;
  }

  return {
    total,
    completed,
    pending: total - completed - skipped - inProgress,
    skipped,
    repeated,
    inProgress,
  };
}
